import React, { useState, useEffect } from "react";
import { Spinner } from "reactstrap";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const WeeklyMigrationChart = ({ species }) => {
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);

  useEffect(() => {
    if (!species) return;

    setLoading(true);
    setError(false);

    fetch(`${API_BASE}/detections/species/weekly?species=${encodeURIComponent(species)}`)
      .then((res) => res.json())
      .then((data) => {
        setWeeklyData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching weekly migration data:", err);
        setError(true);
        setLoading(false);
      });
  }, [species]);

  if (loading) {
    return (
      <div className="text-center p-3">
        <Spinner color="primary" size="sm" />
      </div>
    );
  }

  if (error || !weeklyData) {
    return <p style={{ color: "gray", fontSize: "0.9rem" }}>No data available</p>;
  }

  if (weeklyData.weeklyCounts.length === 0) {
    return <p style={{ color: "gray", fontSize: "0.9rem" }}>No detections recorded for this species</p>;
  }

  const {
    weeklyCounts,
    firstDetection,
    lastDetection,
    firstThisYear,
    lastPreviousSeason,
    totalDetections,
  } = weeklyData;

  // Fill in missing weeks between first and last detection
  const fillMissingWeeks = (weeks) => {
    if (weeks.length === 0) return [];

    const filled = [];
    for (let i = 0; i < weeks.length; i++) {
      filled.push(weeks[i]);

      // Check if there's a gap to the next week
      if (i < weeks.length - 1) {
        const current = weeks[i];
        const next = weeks[i + 1];

        let currentWeek = current.weekNum;
        let currentYear = current.year;

        // Fill gaps between current and next week
        while (true) {
          currentWeek++;
          // Handle year boundary (assume 52 weeks per year)
          if (currentWeek > 52) {
            currentWeek = 1;
            currentYear++;
          }

          // Stop if we've reached the next week
          if (currentYear === next.year && currentWeek === next.weekNum) {
            break;
          }

          // Don't create infinite loops
          if (currentYear > next.year || (currentYear === next.year && currentWeek > next.weekNum)) {
            break;
          }

          // Add a zero-detection week
          filled.push({
            week: `${currentYear}-W${currentWeek.toString().padStart(2, '0')}`,
            year: currentYear,
            weekNum: currentWeek,
            count: 0,
            startDate: null,
            endDate: null
          });
        }
      }
    }

    return filled;
  };

  const allWeeks = fillMissingWeeks(weeklyCounts);

  // Find max count for scaling
  const maxCount = Math.max(...allWeeks.map((w) => w.count), 1);

  // Format date nicely
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Check if a week contains the first or last detection
  const isFirstWeek = (week) => {
    return week.startDate <= firstDetection && week.endDate >= firstDetection;
  };

  const isLastWeek = (week) => {
    return week.startDate <= lastDetection && week.endDate >= lastDetection;
  };

  const formatWeekOf = (week) => {
    if (!week?.startDate) return `Week ${week.weekNum}, ${week.year}`;
    return `Week of ${formatDate(week.startDate)}`;
  };

  const arrivedLabel = firstThisYear || firstDetection;
  const departedLabel = lastPreviousSeason;

  return (
    <div style={{ width: "100%" }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        marginBottom: "16px",
        flexWrap: "wrap",
        gap: "8px"
      }}>
        <h5 style={{ margin: 0, fontWeight: "600", color: "#32325d" }}>
          Migration Timeline
        </h5>
        <div style={{
          display: "flex",
          gap: "20px",
          fontSize: "0.875rem",
          color: "#8898aa"
        }}>
          <span><strong style={{ color: "#32325d" }}>{totalDetections}</strong> total detections</span>
        </div>
      </div>

      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "12px",
        padding: "8px 12px",
        backgroundColor: "#f7fafc",
        borderRadius: "6px",
        fontSize: "0.875rem"
      }}>
        <div>
          <span style={{ color: "#11cdef", fontWeight: "600", marginRight: "6px" }}>●</span>
          <span style={{ color: "#525f7f" }}>Arrived: </span>
          <strong style={{ color: "#32325d" }}>{arrivedLabel ? formatDate(arrivedLabel) : "—"}</strong>
        </div>
        <div>
          <span style={{ color: "#f5365c", fontWeight: "600", marginRight: "6px" }}>●</span>
          <span style={{ color: "#525f7f" }}>Departed: </span>
          <strong style={{ color: "#32325d" }}>{departedLabel ? formatDate(departedLabel) : "—"}</strong>
        </div>
      </div>

      <div style={{
        display: "flex",
        alignItems: "flex-end",
        gap: "3px",
        height: "100px",
        padding: "12px 0",
        borderRadius: "4px",
        overflowX: "auto",
        overflowY: "hidden"
      }}>
        {allWeeks.map((week, index) => {
          const count = week.count;
          const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
          const isFirst = week.startDate && isFirstWeek(week);
          const isLast = week.endDate && isLastWeek(week);
          const hasDetections = count > 0;

          let backgroundColor = "#5e72e4";
          if (!hasDetections) {
            backgroundColor = "#e9ecef"; // Subtle gray for zero weeks
          } else {
            if (isFirst) backgroundColor = "#11cdef";
            if (isLast) backgroundColor = "#f5365c";
            if (isFirst && isLast) backgroundColor = "#fb6340";
          }

          const weekLabel = formatWeekOf(week);
          const dateRange = week.startDate && week.endDate
            ? (week.startDate === week.endDate
                ? formatDate(week.startDate)
                : `${formatDate(week.startDate)} - ${formatDate(week.endDate)}`)
            : 'No detections';

          return (
            <div
              key={index}
              style={{
                flex: "0 0 auto",
                minWidth: "8px",
                width: "12px",
                position: "relative",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end"
              }}
              title={`${weekLabel}\n${dateRange}\n${count} detection${count !== 1 ? 's' : ''}${isFirst ? '\n🔵 First Detection' : ''}${isLast ? '\n🔴 Last Detection' : ''}`}
              onClick={() => setSelectedWeek({ weekLabel, dateRange, count, isFirst, isLast })}
            >
              <div
                style={{
                  height: hasDetections ? `${height}%` : "2px",
                  backgroundColor: backgroundColor,
                  borderRadius: "2px 2px 0 0",
                  transition: "all 0.2s ease",
                  cursor: hasDetections ? "pointer" : "default",
                  minHeight: "2px",
                  boxShadow: (isFirst || isLast) ? "0 0 8px rgba(0,0,0,0.2)" : "none",
                  opacity: hasDetections ? 1 : 0.4
                }}
                onMouseEnter={(e) => {
                  if (hasDetections) {
                    e.target.style.opacity = "0.8";
                  }
                }}
                onMouseLeave={(e) => {
                  if (hasDetections) {
                    e.target.style.opacity = "1";
                  } else {
                    e.target.style.opacity = "0.4";
                  }
                }}
              />
            </div>
          );
        })}
      </div>

      {selectedWeek && (
        <div style={{ marginTop: "10px", fontSize: "0.8rem", color: "#525f7f" }}>
          <strong>{selectedWeek.weekLabel}</strong>: {selectedWeek.count} detection{selectedWeek.count !== 1 ? "s" : ""}
          {" · "}{selectedWeek.dateRange}
        </div>
      )}

      <div style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: "0.75rem",
        color: "#8898aa",
        marginTop: "8px",
        paddingTop: "4px"
      }}>
        {weeklyCounts.length > 0 && (
          <>
            <span>{formatDate(firstDetection)}</span>
            {weeklyCounts.length > 1 && (
              <span>{formatDate(lastDetection)}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WeeklyMigrationChart;
